<script setup lang="ts">
import {computed, onMounted, ref, watch} from 'vue';
import {ElInput} from 'element-plus';
import {CabObj, Filter} from 'common';
import {Filter as FilterIcon, X} from 'lucide-vue-next';
import IconButton from '@/views/ui/IconButton.vue';

const props = defineProps({
    cabList        : Array as () => CabObj[],
    cabListFiltered: Array as () => CabObj[],
    active         : Boolean,
    text           : String,
    filter         : Filter,
    id             : String,
});

const emit = defineEmits(['update:active', 'update:text', 'update:cabListFiltered', 'update:filter']);

const cabListFiltered_ = computed<CabObj[]>(() => filterComputed.value.filter(props.cabList || []));
const activeRef        = ref<boolean>(false);
const textRef          = ref<string>('');
const filterComputed   = computed<Filter>(() => new Filter(activeRef.value, textRef.value));
const numTotal         = computed<number>(() => props.cabList?.length || 0);
const numShown         = computed<number>(() => props.cabListFiltered?.length || 0);
const storageId        = computed<string>(() => 'FILTER-STATE' + (props.id ? `:${props.id}` : ''));

onMounted(() => {
    restoreState();
    emit('update:cabListFiltered', cabListFiltered_.value);
});
watch(storageId, () => restoreState());

watch(cabListFiltered_, x => emit('update:cabListFiltered', x));
watch(() => props.active, x => activeRef.value = x ?? false);
watch(activeRef, x => {
    saveState();
    emit('update:active', x);
});
watch(() => props.text, x => textRef.value = x ?? '');
watch(textRef, x => {
    saveState();
    emit('update:text', x);
});
watch(filterComputed, x => emit('update:filter', x));

function saveState() {
    const state = {
        a: activeRef.value,
        t: textRef.value,
    };
    localStorage.setItem(storageId.value, JSON.stringify(state));
}

function restoreState() {
    const state = localStorage.getItem(storageId.value);
    if (state) {
        const {
            a,
            t,
        }         = JSON.parse(state);
        activeRef.value = a;
        textRef.value   = t;
    }
}

</script>

<template>
    <div class="flex flex-row items-center gap-2">
        <transition name="width-transition">
            <div v-if="activeRef" class="flex flex-row items-center w-[300px]">
                <el-input
                    data-testid="filter-input"
                    class="mr-2"
                    v-model="textRef"
                    type="text"
                    placeholder="filter"
                    title="begin met ! om te inverteren"
                    clearable
                />
                <span class="text-xs text-zzc-muted whitespace-nowrap">{{ numShown }} van {{ numTotal }}</span>
            </div>
        </transition>
        <IconButton :icon="activeRef ? X : FilterIcon" :label="activeRef ? 'filter sluiten' : 'filteren'" :variant="activeRef ? 'primary' : 'default'" data-testid="filter-button" @click="activeRef = !activeRef"/>
    </div>
</template>

<!--suppress CssUnusedSymbol -->
<style scoped>
.width-transition-enter-active,
.width-transition-leave-active {
    transition: opacity 300ms ease, width 300ms ease;
}

.width-transition-enter-from,
.width-transition-leave-to {
    width: 0;
    overflow: hidden;
    opacity: 0;
}

.width-transition-enter-to,
.width-transition-leave-from {
    width: 300px;
    opacity: 1;
}
</style>
